import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import Switch from 'react-switch';

import { absolute } from '../../../components/AnimatedRoutes';
import { getDevice } from '../../../../../mobile/helpers';
import { navigate } from '../../../../../helpers';
import { update } from '../../../../../ducks/app';
import Button from '../../../components/Button';
import Content from '../../../components/Content';
import Header from '../../../components/Header';
import InfoBox from '../../../components/InfoBox';
import Subheader from '../../../components/Subheader';
import profileStyles from '../../Profile/profile.scss';
import requireUser from '../../../../../components/requireUser';
import styles from '../settings.scss';

/* global cordova codePush InstallMode SyncStatus BUILD */

const propTypes = {
  history: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    stackPop: PropTypes.func.isRequired,
  }),
  location: PropTypes.shape({
    state: PropTypes.shape({
      referrer: PropTypes.string,
    }),
  }),
  dispatch: PropTypes.func,
  debug: PropTypes.bool,
  forceMobileMode: PropTypes.bool,
};

const codePushState = (syncStatus) => {
  switch (syncStatus) {
    // Result (final) statuses
    case SyncStatus.UPDATE_INSTALLED:
      return 'The update was installed successfully. For InstallMode.ON_NEXT_RESTART, the changes will be visible after application restart.';
    case SyncStatus.UP_TO_DATE:
      return 'The application is up to date.';
    case SyncStatus.UPDATE_IGNORED:
      return 'The user decided not to install the optional update.';
    case SyncStatus.ERROR:
      return 'An error occured while checking for updates';
    case SyncStatus.IN_PROGRESS:
      return 'Sync already in progress';

    // Intermediate (non final) statuses
    case SyncStatus.CHECKING_FOR_UPDATE:
      return 'Checking for update';
    case SyncStatus.AWAITING_USER_ACTION:
      return 'Awaiting user action';
    case SyncStatus.DOWNLOADING_PACKAGE:
      return 'Downloading package';
    case SyncStatus.INSTALLING_UPDATE:
      return 'Installing update';
    default:
      return syncStatus;
  }
};

class Dev extends React.Component {
  constructor() {
    super();
    this.state = {
      codePushMeta: null,
      version: typeof BUILD !== 'undefined' ? BUILD : 'unknown',
    };
  }

  componentDidMount() {
    if (typeof codePush !== 'undefined') {
      this.onCodePushGetPackage();
    }
    if (typeof cordova !== 'undefined') {
      cordova.getAppVersion.getVersionNumber().then((version) => {
        this.setState({ version });
      });
    }
  }

  onCodePushGetPackage = () => {
    this.setState({ codePushMeta: 'loading...' });
    codePush.getCurrentPackage(
      (pkg) => this.setState({ codePushMeta: pkg || 'running bundled code' }),
      (error) => this.setState({ codePushMeta: error }),
    );
  }

  onCodePushSync = (staging) => () => {
    this.setState({ codePushMeta: 'syncing...' });

    const deploymentKey = getDevice() === 'Android'
      ? 'j1kOhrRnNw-upaMQOlyb-NfYTV2dSJFHQrwgB' // Android staging deploymentKey
      : '_A5-DrCFDosHrE2G8euqSzUxWJuxSk0QfiXgr'; // IOS staging deploymentKey

    try {
      const options = {
        deploymentKey: staging ? deploymentKey : undefined,
        updateDialog: {
          appendReleaseDescription: true,
          descriptionPrefix: '\n\nChange log:\n',
        },
        installMode: InstallMode.IMMEDIATE,
      };

      codePush.sync(
        (state) => this.setState({ codePushMeta: codePushState(state) }),
        options,
        (progress) => this.setState({ codePushMeta: progress }),
        (error) => this.setState({ codePushMeta: error }),
      );
    } catch (err) {
      this.setState({ codePushMeta: err.message });
    }
  }

  mobileMode = (forceMobileMode) => {
    this.props.dispatch(update({ forceMobileMode }));

    if (forceMobileMode) {
      localStorage.setItem('forceMobileMode', true);
    } else {
      localStorage.removeItem('forceMobileMode');
    }
  }

  render() {
    const {
      codePushMeta,
      version,
    } = this.state;
    const {
      history,
      dispatch,
      debug,
      forceMobileMode,
    } = this.props;


    return (
      <>
        <Header
          onBack={() => history.stackPop()}
          right={(
            <div
              className={profileStyles.done}
              role="button"
              tabIndex={0}
              onClick={() => {
                history.stackPop();
                history.stackPop();
              }}
            >
              Done
            </div>
          )}
        >
          Developer console
        </Header>
        <Content>
          <InfoBox>
            This is our developer console. We use it when we test our app on real devices,
            to test new features and to find bugs.
            <p><strong>Be aware that if you change stuff here things may break.</strong></p>
          </InfoBox>
          <Subheader>Build</Subheader>
          <div className={styles.panel}>
            {version}
          </div>

          <Subheader>Quick actions</Subheader>
          <div className={styles.panel}>
            <Button onClick={() => navigate(history, '/app/event/test')}>
              Open test event
            </Button>
          </div>

          <Subheader>Settings</Subheader>
          <div className={styles.panel}>
            <div className={styles.setting}>
              <div className={styles.title} onClick={() => dispatch(update({ debug: !debug }))} role="button" tabIndex="-1">Debug mode</div>
              <Switch
                checked={debug}
                onChange={(checked, event) => {
                  event.stopPropagation();
                  dispatch(update({ debug: checked }));
                }}
                height={12}
                width={32}
                handleDiameter={18}
                uncheckedIcon={false}
                checkedIcon={false}
                onColor="#27ae60"
              />
            </div>

            <div className={styles.setting}>
              <div className={styles.title} onClick={() => this.mobileMode(!forceMobileMode)} role="button" tabIndex="-1">Mobile mode</div>
              <Switch
                checked={forceMobileMode}
                onChange={(checked, event) => {
                  event.stopPropagation();
                  this.mobileMode(checked);
                }}
                height={12}
                width={32}
                handleDiameter={18}
                uncheckedIcon={false}
                checkedIcon={false}
                onColor="#27ae60"
              />
            </div>
          </div>

          {typeof codePush !== 'undefined' && (
            <>
              <Subheader>Code push</Subheader>
              <div className={styles.panel}>
                <pre>
                  { JSON.stringify(codePushMeta, null, 2) }
                </pre>
                <Button onClick={this.onCodePushGetPackage}>
                  Get current
                </Button>
                <Button onClick={this.onCodePushSync(false)}>
                  Sync production
                </Button>
                <Button onClick={this.onCodePushSync(true)}>
                  Sync staging
                </Button>
              </div>
            </>
          )}
        </Content>
      </>
    );
  }
}

Dev.propTypes = propTypes;

const mapStateToProps = (state) => ({
  debug: state.getIn(['app', 'debug']),
  forceMobileMode: state.getIn(['app', 'forceMobileMode']),
});

export default requireUser(connect(mapStateToProps)(absolute(Dev)));
